import React from "react";

import {
  IJobCustomerBranch,
  initialJobCustomerBranch,
} from "../../../redux/customerBranch";
import { IJobCustomer, defaultCustomer } from "../../../redux/job_customer";
import { IBankDetails } from "../../../redux/bankDetails/bankDetails.types";

export const LOCAL_KEY_BILLING_INFO = "billing_info";

export interface IUserBillingInfo {
  customer: IJobCustomer;
  customer_branch: IJobCustomerBranch;
  bank_details?: IBankDetails[];
}

export const saveUserBillingInfoInLocalStore = (
  customer_info: IJobCustomer | null,
  branch_info: IJobCustomerBranch | null,
  bank_details?: IBankDetails[],
) => {
  let localData = localStorage.getItem(LOCAL_KEY_BILLING_INFO);
  if (localData) {
    let existing_billing_info = JSON.parse(localData) as IUserBillingInfo;
    if (customer_info) {
      existing_billing_info.customer = customer_info;
    }
    if (branch_info) {
      existing_billing_info.customer_branch = branch_info;
    }
    if (bank_details) {
      existing_billing_info.bank_details = bank_details;
    }
    localStorage.setItem(
      LOCAL_KEY_BILLING_INFO,
      JSON.stringify(existing_billing_info),
    );
  } else {
    const new_billing_info: IUserBillingInfo = {
      customer: customer_info ? customer_info : defaultCustomer,
      customer_branch: branch_info ? branch_info : initialJobCustomerBranch,
      bank_details: bank_details || [],
    };
    localStorage.setItem(
      LOCAL_KEY_BILLING_INFO,
      JSON.stringify(new_billing_info),
    );
  }
  window.location.reload();
};

export const saveBillingCompanyInStorage = (customer_info: IJobCustomer) => {
  localStorage.setItem(LOCAL_KEY_BILLING_INFO, JSON.stringify(customer_info));
  window.location.reload();
};

export const useBillingInfoFromLocalStore = (): IUserBillingInfo => {
  const [billing_info, setBillingInfo] = React.useState<IUserBillingInfo>(
    () => {
      try {
        const existing_billing_info = localStorage.getItem(
          LOCAL_KEY_BILLING_INFO,
        );
        if (existing_billing_info) return JSON.parse(existing_billing_info);
        return {
          customer: defaultCustomer,
          customer_branch: initialJobCustomerBranch,
          bank_details: [],
        };
      } catch (error) {
        console.error(
          "Error reading localStorage key:",
          LOCAL_KEY_BILLING_INFO,
          error,
        );
        return {
          customer: defaultCustomer,
          customer_branch: initialJobCustomerBranch,
          bank_details: [],
        };
      }
    },
  );

  // Listen for changes in localStorage
  React.useEffect(() => {
    const handleStorageChange = () => {
      try {
        const existing_billing_info = localStorage.getItem(
          LOCAL_KEY_BILLING_INFO,
        );
        if (existing_billing_info) {
          setBillingInfo(JSON.parse(existing_billing_info));
        }
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also check for changes on focus (in case localStorage was updated in the same tab)
    const handleFocus = () => {
      try {
        const existing_billing_info = localStorage.getItem(
          LOCAL_KEY_BILLING_INFO,
        );
        if (existing_billing_info) {
          const newBillingInfo = JSON.parse(existing_billing_info);
          setBillingInfo((prev) => {
            // Only update if the data has actually changed
            if (JSON.stringify(prev) !== JSON.stringify(newBillingInfo)) {
              return newBillingInfo;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error("Error parsing localStorage data on focus:", error);
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  return billing_info;
};

export const useBillingCompanyContext = (): IJobCustomer => {
  const [billing_info] = React.useState<IJobCustomer>(() => {
    try {
      const existing_billing_info = localStorage.getItem(
        LOCAL_KEY_BILLING_INFO,
      );
      return existing_billing_info
        ? JSON.parse(existing_billing_info)
        : defaultCustomer;
    } catch (error) {
      console.error(
        "Error reading localStorage key:",
        LOCAL_KEY_BILLING_INFO,
        error,
      );
      return defaultCustomer;
    }
  });

  return billing_info;
};
